import { useAccount } from '@src/contexts';
import { useMedia } from '@src/hooks';
import ClientApi from '@src/types/client';
import { businessTypeMap, countryMap } from '../data/businessDocumentation';
import { DOCUMENT_REQUIREMENTS } from '../data/businessDocumentsRequirements';
import { BusinessDocumentationFormData } from '../components/businessDocumentationStep';
import { useMemo } from 'react';
import { getBusinessDocumentationSchema } from '../utils';
import { checkNAValues } from '@src/utils/checkNAValues';

interface IUseBusinessDocumentationFormOptions {
  businessInformationDetails:
    | ClientApi.Onboarding.GetBusinessInformationDetails.Response['data']
    | undefined;
}

const useBusinessDocumentationForm = ({
  businessInformationDetails
}: IUseBusinessDocumentationFormOptions) => {
  const { user, userAddress } = useAccount();
  const { isMobile } = useMedia();

  const rawCountry = userAddress?.country; // e.g., 'Nigeria'
  const rawBusinessType = businessInformationDetails?.businessInfomation?.businessType; // e.g., 'Sole Proprietorship'

  const normalizedCountry = countryMap[rawCountry as string];
  const normalizedBusinessType = businessTypeMap[rawBusinessType as string];

  const requiredDocs =
    normalizedCountry &&
    normalizedBusinessType &&
    DOCUMENT_REQUIREMENTS[normalizedCountry]?.[normalizedBusinessType]
      ? DOCUMENT_REQUIREMENTS[normalizedCountry][normalizedBusinessType]
      : [];

  const initialValues: BusinessDocumentationFormData = useMemo(() => {
    // Start with any always-present fields
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const base: any = {
      incorporationCertificate: checkNAValues(
        businessInformationDetails?.businessDocument?.certificateOfIncorporation || null
      ),
      proofOfBusinessAddress: checkNAValues(
        businessInformationDetails?.businessDocument?.proofOfAddress || null
      )
    };

    // Add dynamic fields from requiredDocs
    requiredDocs.forEach((doc) => {
      base[doc.name] = checkNAValues(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (businessInformationDetails?.businessDocument as any)?.[doc.name] || null
      );
    });

    return base;
  }, [businessInformationDetails, requiredDocs]);

  const validationSchema = useMemo(() => {
    return getBusinessDocumentationSchema(requiredDocs);
  }, [requiredDocs]);

  return { validationSchema, initialValues, user, isMobile, requiredDocs };
};

export default useBusinessDocumentationForm;
